import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function ContactPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem('message') as HTMLTextAreaElement).value;
    if (message.length > 500) return;

    setIsSubmitting(true);
    const formData = new URLSearchParams();
    const elements = form.elements;
    for (let i = 0; i < elements.length; i++) {
      const el = elements[i] as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
      if (!el.name) continue;
      if (el.tagName === 'INPUT' && (el as HTMLInputElement).type === 'checkbox') {
        if ((el as HTMLInputElement).checked) formData.append(el.name, el.value);
      } else if (el.tagName === 'INPUT' && (el as HTMLInputElement).type === 'radio') {
        if ((el as HTMLInputElement).checked) formData.append(el.name, el.value);
      } else {
        formData.append(el.name, el.value);
      }
    }

    try {
      const res = await fetch('https://readdy.ai/api/form/d6pod9s4k19g20dvquu0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });
      if (res.ok) {
        setSubmitStatus('success');
        form.reset();
        setCharCount(0);
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-white shadow-sm'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-2 cursor-pointer">
            <div className="w-8 h-8 bg-slate-800 rounded-sm flex items-center justify-center">
              <span className="text-white font-bold text-sm">N</span>
            </div>
            <span className="font-bold text-lg text-slate-800">虹橋</span>
          </Link>
          <div className="flex items-center space-x-8">
            <Link to="/#about" className="text-sm font-medium text-slate-700 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">企業情報</Link>
            <Link to="/#services" className="text-sm font-medium text-slate-700 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">サービス</Link>
            <Link to="/#testimonials" className="text-sm font-medium text-slate-700 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">お客様の声</Link>
            <Link to="/contact" className="text-sm font-medium text-teal-600 whitespace-nowrap cursor-pointer border-b border-teal-600">お問い合わせ</Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative pt-32 pb-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-xs font-light tracking-widest text-teal-600 mb-4">CONTACT</p>
          <h1 className="text-4xl lg:text-5xl font-light text-slate-800 mb-4" style={{ fontFamily: 'serif' }}>お問い合わせ</h1>
          <p className="text-sm text-slate-500 leading-relaxed">
            ご質問・ご相談はお気軽にお問い合わせください。<br />
            担当者より2営業日以内にご連絡いたします。
          </p>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-20">
        <div className="max-w-3xl mx-auto px-6">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-10">

            {submitStatus === 'success' && (
              <div className="mb-8 p-5 bg-teal-50 border border-teal-200 rounded-xl flex items-start space-x-3">
                <div className="w-5 h-5 flex items-center justify-center text-teal-600 mt-0.5">
                  <i className="ri-checkbox-circle-line text-xl"></i>
                </div>
                <div>
                  <p className="text-sm font-medium text-teal-800">送信が完了しました</p>
                  <p className="text-xs text-teal-600 mt-1">お問い合わせありがとうございます。担当者よりご連絡いたします。</p>
                </div>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="mb-8 p-5 bg-red-50 border border-red-200 rounded-xl flex items-start space-x-3">
                <div className="w-5 h-5 flex items-center justify-center text-red-500 mt-0.5">
                  <i className="ri-error-warning-line text-xl"></i>
                </div>
                <div>
                  <p className="text-sm font-medium text-red-700">送信に失敗しました</p>
                  <p className="text-xs text-red-500 mt-1">しばらく経ってから再度お試しください。</p>
                </div>
              </div>
            )}

            <form data-readdy-form onSubmit={handleSubmit} className="space-y-6">
              {/* Name */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    お名前 <span className="text-red-400 text-xs">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    placeholder="山田 太郎"
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-teal-500 transition-colors bg-gray-50 focus:bg-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    フリガナ
                  </label>
                  <input
                    type="text"
                    name="furigana"
                    placeholder="ヤマダ タロウ"
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-teal-500 transition-colors bg-gray-50 focus:bg-white"
                  />
                </div>
              </div>

              {/* Email & Phone */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    メールアドレス <span className="text-red-400 text-xs">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="example@company.co.jp"
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-teal-500 transition-colors bg-gray-50 focus:bg-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    電話番号
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    placeholder="03-0000-0000"
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-teal-500 transition-colors bg-gray-50 focus:bg-white"
                  />
                </div>
              </div>

              {/* Company */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  会社名・組織名
                </label>
                <input
                  type="text"
                  name="company"
                  placeholder="株式会社〇〇"
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-teal-500 transition-colors bg-gray-50 focus:bg-white"
                />
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  お問い合わせ種別 <span className="text-red-400 text-xs">*</span>
                </label>
                <select
                  name="category"
                  required
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-teal-500 transition-colors bg-gray-50 focus:bg-white cursor-pointer"
                >
                  <option value="">選択してください</option>
                  <option value="資料請求">資料請求</option>
                  <option value="コンサルティングについて">コンサルティングについて</option>
                  <option value="システム開発について">システム開発について</option>
                  <option value="デジタル戦略について">デジタル戦略について</option>
                  <option value="採用について">採用について</option>
                  <option value="その他">その他</option>
                </select>
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  お問い合わせ内容 <span className="text-red-400 text-xs">*</span>
                </label>
                <textarea
                  name="message"
                  required
                  rows={6}
                  maxLength={500}
                  placeholder="お問い合わせ内容をご記入ください"
                  onChange={(e) => setCharCount(e.target.value.length)}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-teal-500 transition-colors bg-gray-50 focus:bg-white resize-none"
                />
                <p className={`text-xs mt-1 text-right ${charCount > 500 ? 'text-red-500' : 'text-slate-400'}`}>
                  {charCount} / 500文字
                </p>
              </div>

              {/* Privacy */}
              <div className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  name="privacy"
                  id="privacy"
                  required
                  className="mt-0.5 w-4 h-4 accent-teal-600 cursor-pointer"
                />
                <label htmlFor="privacy" className="text-xs text-slate-500 leading-relaxed cursor-pointer">
                  <a href="#" className="text-teal-600 underline hover:text-teal-700">プライバシーポリシー</a>に同意の上、送信します。
                </label>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={isSubmitting || charCount > 500}
                className="w-full bg-slate-800 text-white py-3.5 rounded-lg text-sm font-medium hover:bg-slate-700 transition-colors flex items-center justify-center space-x-2 whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    <span>送信中...</span>
                  </>
                ) : (
                  <>
                    <span>送信する</span>
                    <i className="ri-send-plane-line"></i>
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Info Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-10">
            <div className="bg-slate-50 rounded-xl p-6 text-center">
              <div className="w-10 h-10 flex items-center justify-center mx-auto mb-3 text-teal-600">
                <i className="ri-mail-line text-2xl"></i>
              </div>
              <p className="text-xs font-medium text-slate-700 mb-1">メール</p>
              <p className="text-xs text-slate-500">info@nijihashi.co.jp</p>
            </div>
            <div className="bg-slate-50 rounded-xl p-6 text-center">
              <div className="w-10 h-10 flex items-center justify-center mx-auto mb-3 text-teal-600">
                <i className="ri-phone-line text-2xl"></i>
              </div>
              <p className="text-xs font-medium text-slate-700 mb-1">電話</p>
              <p className="text-xs text-slate-500">03-0000-0000</p>
            </div>
            <div className="bg-slate-50 rounded-xl p-6 text-center">
              <div className="w-10 h-10 flex items-center justify-center mx-auto mb-3 text-teal-600">
                <i className="ri-time-line text-2xl"></i>
              </div>
              <p className="text-xs font-medium text-slate-700 mb-1">受付時間</p>
              <p className="text-xs text-slate-500">平日 9:00〜18:00</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-800 text-white py-8 mt-10">
        <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
          <div className="flex items-center space-x-2">
            <div className="w-7 h-7 bg-white rounded-sm flex items-center justify-center">
              <span className="text-slate-800 font-bold text-xs">N</span>
            </div>
            <span className="font-bold text-base">虹橋株式会社</span>
          </div>
          <p className="text-xs text-gray-400">© 虹橋株式会社 2025 All rights reserved</p>
        </div>
      </footer>
    </div>
  );
}
